import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for the admin new conversation form.
 *
 * Handles switching between admin→parent and admin→teacher
 * conversation types, and dynamically loading recipient lists.
 *
 * Works with SlimSelect which auto-initializes on all <select>
 * elements — uses SlimSelect's setData API to update options.
 */
export default class extends Controller {
  static targets = [
    "typeRadio", "parentField", "teacherField",
    "parentSelect", "teacherSelect",
  ]
  static values = {
    parentsUrl: String,
    teachersUrl: String,
  }

  connect() {
    // Small delay to let SlimSelect initialize first
    setTimeout(() => {
      this.loadParents()
      this.loadTeachers()
    }, 100)
  }

  typeChanged() {
    const type = this.currentType
    if (type === "admin_parent") {
      this.parentFieldTarget.classList.remove("hidden")
      this.teacherFieldTarget.classList.add("hidden")
    } else {
      this.parentFieldTarget.classList.add("hidden")
      this.teacherFieldTarget.classList.remove("hidden")
    }
  }

  get currentType() {
    const checked = this.typeRadioTargets.find((r) => r.checked)
    return checked ? checked.value : "admin_parent"
  }

  async loadParents() {
    try {
      const response = await fetch(this.parentsUrlValue, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      if (!response.ok) return
      const parents = await response.json()
      this.populateSelect(this.parentSelectTarget, parents)
    } catch (err) {
      console.error("[admin-new-conversation] loadParents error:", err)
    }
  }

  async loadTeachers() {
    try {
      const response = await fetch(this.teachersUrlValue, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      if (!response.ok) return
      const teachers = await response.json()
      this.populateSelect(this.teacherSelectTarget, teachers)
    } catch (err) {
      console.error("[admin-new-conversation] loadTeachers error:", err)
    }
  }

  populateSelect(select, items) {
    // Préserver le placeholder affiché ("Choisir…") pour que la ré-initialisation
    // SlimSelect le reprenne comme texte par défaut.
    const placeholderText =
      select.dataset.placeholder ||
      (select.options[0] && select.options[0].value === ""
        ? select.options[0].text
        : "")

    // Repeupler la balise <select> native plutôt que d'utiliser setData : on veut
    // que SlimSelect recalcule l'affichage de la recherche à partir du nombre réel
    // d'options (la recherche apparaît au-delà de 7 — DS §11). L'instance initiale
    // a été créée au `connect`, avant le chargement Ajax, alors que le select ne
    // comptait qu'une option et n'avait donc pas de barre de recherche.
    select.innerHTML = ""
    const placeholder = document.createElement("option")
    placeholder.value = ""
    placeholder.textContent = placeholderText
    select.appendChild(placeholder)

    items.forEach((item) => {
      const option = document.createElement("option")
      option.value = String(item.id)
      option.textContent = item.name
      select.appendChild(option)
    })

    this.reinitSlimSelect(select)
  }

  // Détruit l'habillage SlimSelect existant puis le recrée via le helper global,
  // ce qui réévalue `showSearch` sur la liste complète. Même approche que les
  // contrôleurs parent-user-search / staff-search.
  reinitSlimSelect(select) {
    if (select.dataset.slimInitialized === "true") {
      // destroy() retire le panneau `.ss-content` ajouté au <body> ET le wrapper
      // `.ss-main` : sans cela, l'instance pré-Ajax laisse un panneau orphelin.
      if (select.slim && typeof select.slim.destroy === "function") {
        try {
          select.slim.destroy()
        } catch (e) {
          /* instance déjà détruite — on poursuit */
        }
      }
      const sibling = select.nextElementSibling
      if (sibling && sibling.classList.contains("ss-main")) sibling.remove()
      select.dataset.slimInitialized = "false"
      select.style.display = ""
    }
    if (typeof window.initSlimSelects === "function") {
      window.initSlimSelects()
    }
  }
}
